import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  Upload, Type, Image as ImageIcon, Trash2, Eye, EyeOff, Lock, Unlock,
  ChevronUp, ChevronDown, ArrowLeft, Save, Send, Search, Plus,
  Loader2, Check, Layers, RotateCw, BookmarkPlus
} from 'lucide-react';
import AppHeader from '../components/AppHeader';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';

const SIDES = ['front', 'back', 'left', 'right'];
const FONTS = [
  'TT Octosquares ExtraBold',
  'Impact', 'Arial Black', 'Arial', 'Helvetica', 'Georgia',
  'Times New Roman', 'Verdana', 'Courier New', 'Comic Sans MS', 'Trebuchet MS'
];
const CANVAS_SIZE = 600;
const uid = () => Math.random().toString(36).slice(2, 11);

const blankSide = () => ({ background: null, layers: [] });

const MockupDesignerV2 = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const proposalId = searchParams.get('proposal');
  const itemIndex = parseInt(searchParams.get('item') || '0');

  const canvasRef = useRef(null);
  const dragState = useRef({ dragging: false, startX: 0, startY: 0, layerStartX: 0, layerStartY: 0 });

  const [proposal, setProposal] = useState(null);
  const [currentItem, setCurrentItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  const [currentSide, setCurrentSide] = useState('front');
  const [sides, setSides] = useState({
    front: blankSide(), back: blankSide(), left: blankSide(), right: blankSide()
  });
  const [activeLayerId, setActiveLayerId] = useState(null);

  // Product search
  const [searchCode, setSearchCode] = useState('');
  const [searching, setSearching] = useState(false);
  const [colorOptions, setColorOptions] = useState([]);

  // Design library
  const [designs, setDesigns] = useState([]);
  const [showDesignLibrary, setShowDesignLibrary] = useState(false);

  // "Save to Catalog" modal — capture the current canvas background as a new internal-catalog color
  const [showSaveCatalog, setShowSaveCatalog] = useState(false);
  const [savingCatalog, setSavingCatalog] = useState(false);
  const [catalogForm, setCatalogForm] = useState({
    style_code: '', brand: '', style_name: '', color_name: '', hex: '#000000',
  });

  // Preload custom mockup fonts. Browsers don't fetch a webfont until something
  // on the page actually uses it — but the mockup canvas draws with the font
  // before any DOM node has rendered it. Forcing document.fonts.load() ensures
  // canvas.fillText() finds the right glyphs on the very first export.
  useEffect(() => {
    if (typeof document !== 'undefined' && document.fonts && document.fonts.load) {
      document.fonts.load('48px "TT Octosquares ExtraBold"').catch(() => {});
    }
  }, []);

  // Load proposal + designs
  useEffect(() => {
    const load = async () => {
      try {
        const [pRes, dRes] = await Promise.all([
          axios.get(`/proposals/${proposalId}`),
          axios.get('/design/designs').catch(() => ({ data: [] })),
        ]);
        setProposal(pRes.data);
        setDesigns(dRes.data || []);
        const item = pRes.data.items?.[itemIndex];
        if (item) {
          setCurrentItem(item);
          // Prefer the new mockup_designs collection (offloaded heavy state). Fall back to
          // legacy inline `mockup_data` for proposals saved before this refactor.
          let restoredSides = null;
          if (item.mockup_design_id || item.mockup_data?.sides == null) {
            try {
              const dRes2 = await axios.get(`/proposals/${proposalId}/items/${itemIndex}/mockup-design`);
              if (dRes2.data?.sides && Object.keys(dRes2.data.sides).length > 0) {
                restoredSides = dRes2.data.sides;
              }
            } catch (err) {
              console.warn('No saved mockup-design (this is OK if new item)', err?.response?.status);
            }
          }
          if (!restoredSides && item.mockup_data?.sides) {
            restoredSides = item.mockup_data.sides;
          }
          if (restoredSides) {
            const out = { front: blankSide(), back: blankSide(), left: blankSide(), right: blankSide() };
            SIDES.forEach(s => {
              if (restoredSides[s]) out[s] = restoredSides[s];
            });
            setSides(out);
          } else if (item.image_url) {
            setSides(prev => ({ ...prev, front: { ...prev.front, background: item.image_url } }));
          }
        }
      } catch (e) {
        console.error(e);
        toast.error('Failed to load order');
      } finally {
        setLoading(false);
      }
    };
    if (proposalId) load();
  }, [proposalId, itemIndex]);

  // One-shot auto-regenerate: when a proposal card asks us to refresh the saved
  // composites (e.g. after the contain-fit fix), we re-run renderComposite over
  // the loaded layers and persist the new images — no user interaction needed.
  const autoRegenRef = useRef(false);
  useEffect(() => {
    if (autoRegenRef.current) return;
    if (loading) return;
    if (searchParams.get('regenerate') !== '1') return;
    const hasAnyLayer = SIDES.some(s => (sides[s]?.layers || []).length > 0);
    if (!hasAnyLayer) return;
    autoRegenRef.current = true;
    (async () => {
      const ok = await saveMockup();
      if (ok) {
        toast.success('Mockup re-rendered with correct proportions');
        // Drop the flag from the URL so a manual refresh doesn't re-trigger.
        navigate(`/mockup-designer?proposal=${proposalId}&item=${itemIndex}`, { replace: true });
      }
    })();
  }, [loading, sides]);

  const side = sides[currentSide];
  const activeLayer = side?.layers?.find(l => l.id === activeLayerId);

  const updateSide = (updater) => {
    setSides(prev => ({ ...prev, [currentSide]: updater(prev[currentSide]) }));
  };

  // Apply a color's 4 view photos onto each canvas side at once. Sides without a matching
  // view photo fall back to a flat hex fill so the designer can still drop logos there.
  const loadColorAcrossAllSides = (colorObj, hex = null) => {
    if (!colorObj) return;
    const v = colorObj.views || {};
    const fallbackImg = colorObj.image_url || v.front || null;
    const fallbackHex = hex || (/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test((colorObj.hex || '').toString())
      ? colorObj.hex : '#e5e7eb');
    setSides(prev => ({
      front: { ...prev.front, background: v.front || fallbackImg, background_color: fallbackHex },
      back:  { ...prev.back,  background: v.back  || null,        background_color: fallbackHex },
      left:  { ...prev.left,  background: v.left_sleeve  || null, background_color: fallbackHex },
      right: { ...prev.right, background: v.right_sleeve || null, background_color: fallbackHex },
    }));
  };

  const updateLayer = (id, patch) => {
    updateSide(s => ({
      ...s,
      layers: s.layers.map(l => (l.id === id ? { ...l, ...patch } : l))
    }));
  };

  const updateActive = (patch) => activeLayer && updateLayer(activeLayer.id, patch);

  // ===== Background (product mockup) =====
  const handleBackgroundUpload = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const r = new FileReader();
    r.onload = (ev) => updateSide(s => ({ ...s, background: ev.target.result }));
    r.readAsDataURL(f);
  };

  const searchProduct = async () => {
    if (!searchCode.trim()) return;
    setSearching(true);
    try {
      const code = searchCode.trim();

      // 1) TRY INTERNAL CATALOG FIRST — these are Stares Group's own real product photos.
      try {
        const internal = await axios.get(`/internal-catalog/search/${encodeURIComponent(code)}`);
        const data = internal.data || {};
        const colors = (data.colors || []).map((c) => {
          // Build a per-view map. Legacy `image_base64` counts as front when views is missing.
          const v = c.views || {};
          const views = {
            front: v.front || c.image_base64 || null,
            back: v.back || null,
            left_sleeve: v.left_sleeve || null,
            right_sleeve: v.right_sleeve || null,
          };
          return {
            color_name: c.color_name,
            hex: c.hex || null,
            image_url: views.front, // displayed in swatch grid
            views,                  // available to the side-loader on click
            supplier: 'Internal Catalog',
            brand: data.brand,
            style: data.style_code,
          };
        });
        if (colors.length) {
          setColorOptions(colors);
          const first = colors[0];
          const firstHex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test((first.hex || '').toString())
            ? first.hex : '#e5e7eb';
          loadColorAcrossAllSides(first, firstHex);
          toast.success(`Loaded ${data.brand} ${data.style_code} from your catalog · ${colors.length} colors`);
          return;
        }
        // Internal style exists but has no colors — fall through to vendor search so the
        // user at least sees vendor-known colors. (Photos will be hex squares.)
      } catch (err) {
        if (err.response && err.response.status !== 404) {
          // network/server error — surface it
          throw err;
        }
      }

      // 2) Fallback: multi-vendor search (S&S + SanMar). Renders as hex swatches because
      //    vendor CDNs reject hot-linking.
      let products = [];
      try {
        const res = await axios.get(`/products/search/all/${encodeURIComponent(code)}`);
        products = Array.isArray(res.data) ? res.data : (res.data ? [res.data] : []);
      } catch (vendorErr) {
        if (vendorErr.response && vendorErr.response.status === 404) {
          products = [];
        } else {
          throw vendorErr;
        }
      }
      if (!products.length) {
        setColorOptions([]);
        toast.error('Style not found in your catalog or vendor APIs');
        return;
      }

      // SanMar/S&S CDNs reject direct hot-linking and return placeholder JPEGs,
      // so we don't try to use vendor image URLs at all.
      const normalized = [];
      const seen = new Set();
      for (const p of products) {
        const supplier = p.supplier || 'S&S Activewear';
        const brand = p.brandName || p.brand_name || '';
        const style = p.styleID || p.style || code;
        for (const c of (p.colors || [])) {
          const name = c.colorName || c.color_name || c.name || 'Color';
          let img = null;
          if (Array.isArray(c.images) && c.images.length && c.images[0].startsWith('data:')) {
            img = c.images[0];
          } else if (c.image_url && c.image_url.startsWith('data:')) {
            img = c.image_url;
          }

          const key = name.toLowerCase();
          if (seen.has(key)) continue;
          seen.add(key);
          normalized.push({
            color_name: name,
            hex: c.hex || null,
            image_url: img,
            supplier,
            brand,
            style,
          });
        }
      }

      if (!normalized.length) {
        setColorOptions([]);
        toast.error('Found the style, but no color data is available');
        return;
      }

      setColorOptions(normalized);
      const first = normalized[0];
      const firstHex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test((first.hex || '').toString())
        ? first.hex : '#e5e7eb';
      updateSide(s => ({ ...s, background: first.image_url || null, background_color: firstHex }));
      const firstP = products[0];
      toast.success(`Loaded ${firstP.brandName || firstP.brand_name || ''} ${firstP.styleID || firstP.style || code} · ${normalized.length} colors`);
    } catch (e) {
      setColorOptions([]);
      const detail = e.response?.data?.detail || 'Style not found in catalog';
      toast.error(detail);
    } finally {
      setSearching(false);
    }
  };

  const useItemImage = () => {
    if (currentItem?.image_url) {
      updateSide(s => ({ ...s, background: currentItem.image_url }));
      toast.success('Using product image from order');
    } else {
      toast.error('No product image available for this item');
    }
  };

  // Open the "Save to Catalog" modal pre-filled from whatever's on the canvas + order context.
  const openSaveToCatalog = () => {
    if (!side.background) {
      toast.error('Upload or pick a product photo first');
      return;
    }
    if (!side.background.startsWith('data:')) {
      toast.error('Only uploaded photos can be saved (vendor URLs are placeholders)');
      return;
    }
    setCatalogForm({
      style_code: searchCode.trim() || (currentItem?.style || ''),
      brand: currentItem?.brand || (colorOptions[0]?.brand || ''),
      style_name: currentItem?.product_name || '',
      color_name: currentItem?.color || '',
      hex: side.background_color && /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(side.background_color)
        ? side.background_color : '#000000',
    });
    setShowSaveCatalog(true);
  };

  const saveCanvasToCatalog = async () => {
    const f = catalogForm;
    if (!f.style_code.trim() || !f.brand.trim() || !f.style_name.trim() || !f.color_name.trim()) {
      toast.error('Style code, brand, style name, and color are all required');
      return;
    }
    if (!side.background?.startsWith('data:')) {
      toast.error('Background must be an uploaded image');
      return;
    }
    setSavingCatalog(true);
    try {
      // 1) Find or create the style
      let itemId = null;
      try {
        const found = await axios.get(`/internal-catalog/search/${encodeURIComponent(f.style_code)}`);
        itemId = found.data?.id;
      } catch (err) {
        if (err.response?.status !== 404) throw err;
      }
      if (!itemId) {
        const created = await axios.post('/internal-catalog', {
          style_code: f.style_code.trim(),
          brand: f.brand.trim(),
          style_name: f.style_name.trim(),
        });
        itemId = created.data?.id;
      }
      // 2) Add (or replace) the color
      await axios.post(`/internal-catalog/${itemId}/colors`, {
        color_name: f.color_name.trim(),
        hex: f.hex || null,
        image_base64: side.background,
      });
      toast.success(`Saved ${f.color_name} to ${f.brand} ${f.style_code} in your Catalog`);
      setShowSaveCatalog(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save to catalog');
    } finally {
      setSavingCatalog(false);
    }
  };

  // ===== Layers =====
  const addImageLayer = (src, name = 'Image') => {
    const layer = {
      id: uid(), type: 'image', name, src, visible: true, locked: false,
      x: CANVAS_SIZE / 2 - 90, y: CANVAS_SIZE / 2 - 90, width: 180, height: 180,
      rotation: 0, opacity: 1
    };
    updateSide(s => ({ ...s, layers: [...s.layers, layer] }));
    setActiveLayerId(layer.id);
  };

  const handleImageUpload = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const r = new FileReader();
    r.onload = (ev) => addImageLayer(ev.target.result, f.name);
    r.readAsDataURL(f);
  };

  const addTextLayer = () => {
    const layer = {
      id: uid(), type: 'text', name: 'Text Layer', text: 'YOUR TEXT',
      visible: true, locked: false,
      x: CANVAS_SIZE / 2 - 100, y: CANVAS_SIZE / 2 - 30, width: 200, height: 60,
      rotation: 0, opacity: 1,
      color: '#ffffff', fontSize: 42, fontFamily: 'Impact',
      bold: true, italic: false,
      stroke: true, strokeColor: '#000000', strokeWidth: 2,
      arch: 0,
    };
    updateSide(s => ({ ...s, layers: [...s.layers, layer] }));
    setActiveLayerId(layer.id);
  };

  const deleteLayer = (id) => {
    updateSide(s => ({ ...s, layers: s.layers.filter(l => l.id !== id) }));
    if (activeLayerId === id) setActiveLayerId(null);
  };

  const moveLayer = (id, dir) => {
    updateSide(s => {
      const idx = s.layers.findIndex(l => l.id === id);
      if (idx === -1) return s;
      const next = idx + dir;
      if (next < 0 || next >= s.layers.length) return s;
      const layers = [...s.layers];
      [layers[idx], layers[next]] = [layers[next], layers[idx]];
      return { ...s, layers };
    });
  };

  // ===== Drag =====
  const onCanvasMouseDown = (e) => {
    if (!activeLayer || activeLayer.locked) return;
    const rect = canvasRef.current.getBoundingClientRect();
    dragState.current = {
      dragging: true,
      startX: e.clientX,
      startY: e.clientY,
      layerStartX: activeLayer.x,
      layerStartY: activeLayer.y,
      scaleX: CANVAS_SIZE / rect.width,
      scaleY: CANVAS_SIZE / rect.height,
    };
  };

  const onCanvasMouseMove = (e) => {
    const d = dragState.current;
    if (!d.dragging || !activeLayer) return;
    const dx = (e.clientX - d.startX) * d.scaleX;
    const dy = (e.clientY - d.startY) * d.scaleY;
    updateLayer(activeLayer.id, { x: d.layerStartX + dx, y: d.layerStartY + dy });
  };

  const onCanvasMouseUp = () => { dragState.current.dragging = false; };

  // ===== Render Text (with optional arch via SVG) =====
  const renderTextLayer = (l) => {
    const fontWeight = l.bold ? 'bold' : 'normal';
    const fontStyle = l.italic ? 'italic' : 'normal';
    if (l.arch && Math.abs(l.arch) > 5) {
      // SVG arched text
      const w = Math.max(l.width, 100);
      const h = Math.max(l.height, 80);
      const archAmount = (l.arch / 100) * (h / 2);
      // Path: curved horizontally
      const startX = 10;
      const endX = w - 10;
      const midY = h / 2 - archAmount;
      const baselineY = h / 2;
      const pathD = `M ${startX} ${baselineY} Q ${w / 2} ${midY * 2 - baselineY} ${endX} ${baselineY}`;
      return (
        <svg width={w} height={h} style={{ overflow: 'visible' }}>
          <defs>
            <path id={`p-${l.id}`} d={pathD} fill="transparent" />
          </defs>
          <text
            fontSize={l.fontSize}
            fontFamily={l.fontFamily}
            fontWeight={fontWeight}
            fontStyle={fontStyle}
            fill={l.color}
            stroke={l.stroke ? l.strokeColor : 'none'}
            strokeWidth={l.stroke ? l.strokeWidth : 0}
            paintOrder="stroke"
            textAnchor="middle"
          >
            <textPath href={`#p-${l.id}`} startOffset="50%">{l.text}</textPath>
          </text>
        </svg>
      );
    }
    return (
      <div
        style={{
          fontSize: l.fontSize, fontFamily: l.fontFamily,
          fontWeight, fontStyle, color: l.color,
          WebkitTextStroke: l.stroke ? `${l.strokeWidth}px ${l.strokeColor}` : 'none',
          whiteSpace: 'pre', lineHeight: 1.1,
        }}
      >
        {l.text}
      </div>
    );
  };

  // ===== Save: render composite for each side =====
  const renderComposite = async (sideData) => {
    const canvas = document.createElement('canvas');
    canvas.width = CANVAS_SIZE; canvas.height = CANVAS_SIZE;
    const ctx = canvas.getContext('2d');
    // Background
    if (sideData.background) {
      const img = new window.Image();
      img.crossOrigin = 'anonymous';
      try {
        await new Promise((res, rej) => {
          img.onload = res; img.onerror = rej; img.src = sideData.background;
        });
        // Contain fit
        const ratio = Math.min(CANVAS_SIZE / img.width, CANVAS_SIZE / img.height);
        const w = img.width * ratio, h = img.height * ratio;
        const x = (CANVAS_SIZE - w) / 2, y = (CANVAS_SIZE - h) / 2;
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
        ctx.drawImage(img, x, y, w, h);
      } catch {
        ctx.fillStyle = '#f3f4f6';
        ctx.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
      }
    } else {
      ctx.fillStyle = '#f3f4f6';
      ctx.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
    }
    // Layers (in order)
    for (const l of sideData.layers) {
      if (!l.visible) continue;
      ctx.save();
      ctx.globalAlpha = l.opacity ?? 1;
      const cx = l.x + l.width / 2, cy = l.y + l.height / 2;
      ctx.translate(cx, cy);
      ctx.rotate((l.rotation || 0) * Math.PI / 180);
      ctx.translate(-cx, -cy);
      if (l.type === 'image' && l.src) {
        const img = new window.Image();
        img.crossOrigin = 'anonymous';
        try {
          await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = l.src; });
          // Match the live canvas's `object-contain` behaviour: scale the image to fit
          // inside the layer box without stretching, then center it. Drawing with the
          // raw box width/height (as we used to) squashes wide logos into tall boxes
          // (e.g. hat fronts) and vice versa, producing distorted composites.
          const iw = img.width || 1;
          const ih = img.height || 1;
          const fitRatio = Math.min(l.width / iw, l.height / ih);
          const dw = iw * fitRatio;
          const dh = ih * fitRatio;
          const dx = l.x + (l.width - dw) / 2;
          const dy = l.y + (l.height - dh) / 2;
          ctx.drawImage(img, dx, dy, dw, dh);
        } catch {/* skip */}
      } else if (l.type === 'text') {
        ctx.font = `${l.italic ? 'italic ' : ''}${l.bold ? 'bold ' : ''}${l.fontSize}px ${l.fontFamily}`;
        ctx.fillStyle = l.color;
        ctx.textBaseline = 'middle';
        ctx.textAlign = 'center';
        if (l.stroke) {
          ctx.lineWidth = l.strokeWidth;
          ctx.strokeStyle = l.strokeColor;
          ctx.strokeText(l.text, cx, cy);
        }
        ctx.fillText(l.text, cx, cy);
      }
      ctx.restore();
    }
    return canvas.toDataURL('image/png');
  };

  const buildAllComposites = async () => {
    const result = {};
    for (const s of SIDES) {
      result[s] = await renderComposite(sides[s]);
    }
    return result;
  };

  // Down-scale a data: URI PNG to a small JPEG thumbnail for the proposal item.
  // Keeps the proposal table preview cheap and emailable.
  const makeThumbnail = async (dataUrl, size = 200) => {
    return new Promise((resolve) => {
      const img = new window.Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = size; canvas.height = size;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, size, size);
        const ratio = Math.min(size / img.width, size / img.height);
        const w = img.width * ratio, h = img.height * ratio;
        ctx.drawImage(img, (size - w) / 2, (size - h) / 2, w, h);
        resolve(canvas.toDataURL('image/jpeg', 0.78));
      };
      img.onerror = () => resolve(dataUrl);
      img.src = dataUrl;
    });
  };

  const saveMockup = async () => {
    setSaving(true);
    try {
      const composites = await buildAllComposites();
      // Lightweight thumbnail (200x200 JPEG) — small enough to embed on the proposal item
      // for table previews + email-friendly. The full-res composites live in mockup_views.
      const thumbnail = await makeThumbnail(composites.front, 200);

      // 1) Save heavy state into the dedicated mockup_designs collection.
      try {
        await axios.put(
          `/proposals/${proposalId}/items/${itemIndex}/mockup-design`,
          {
            sides,
            mockup_views: composites,
            mockup_url: thumbnail,
          }
        );
      } catch (err) {
        if (err.response?.status === 413) {
          toast.error(err.response.data?.detail || 'Mockup is too large to save');
          return false;
        }
        throw err;
      }

      // 2) Refresh the local proposal so we have the updated lightweight item.
      try {
        const refreshed = await axios.get(`/proposals/${proposalId}`);
        setProposal(refreshed.data);
      } catch { /* non-fatal */ }

      toast.success('Mockup saved');
      return true;
    } catch (e) {
      console.error(e);
      toast.error('Failed to save mockup');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const sendForApproval = async () => {
    setSending(true);
    try {
      const ok = await saveMockup();
      if (!ok) return;
      await axios.post(`/proposals/${proposalId}/send-design-approval`, { item_index: itemIndex });
      toast.success('Approval email sent to customer');
    } catch (e) {
      console.error(e);
      toast.error('Failed to send approval');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="mockup-designer-page">
      <AppHeader />
      <main className="max-w-[1600px] mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate(`/proposal/${proposalId}`)} data-testid="back-to-order-btn">
              <ArrowLeft className="w-4 h-4 mr-2" />Back to Order
            </Button>
            <div>
              <h1 className="text-2xl font-bold">Mockup Designer</h1>
              <p className="text-gray-600 text-sm">
                {currentItem?.brand} {currentItem?.style} {currentItem?.color ? `(${currentItem.color})` : ''} · Item {itemIndex + 1} of {proposal?.items?.length}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={saveMockup} disabled={saving} data-testid="save-mockup-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save
            </Button>
            <Button onClick={sendForApproval} disabled={sending} className="bg-green-600 hover:bg-green-700" data-testid="send-approval-btn">
              {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Send for Approval
            </Button>
          </div>
        </div>

        {/* Approval status badge */}
        {currentItem?.approval_status && (
          <div className={`mb-4 px-4 py-2 rounded-lg inline-flex items-center gap-2 text-sm ${
            currentItem.approval_status === 'approved' ? 'bg-green-100 text-green-800' :
            currentItem.approval_status === 'changes_requested' ? 'bg-yellow-100 text-yellow-800' :
            'bg-blue-100 text-blue-800'
          }`}>
            <Check className="w-4 h-4" />
            Status: {currentItem.approval_status.replace('_', ' ').toUpperCase()}
            {currentItem.approval_feedback && <span className="ml-2 italic">"{currentItem.approval_feedback}"</span>}
          </div>
        )}

        <div className="grid grid-cols-12 gap-4">
          {/* LEFT: Product Picker */}
          <div className="col-span-12 lg:col-span-3 space-y-3">
            <div className="bg-white rounded-xl shadow-sm p-4">
              <h3 className="font-semibold text-sm mb-3 flex items-center gap-2">
                <Search className="w-4 h-4" />Product Source
              </h3>
              <Label className="text-xs">Search style (S&S/SanMar)</Label>
              <div className="flex gap-2 mt-1 mb-3">
                <Input
                  placeholder="e.g. G500, NL3600"
                  value={searchCode}
                  onChange={(e) => setSearchCode(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && searchProduct()}
                  data-testid="product-search-input"
                />
                <Button size="sm" onClick={searchProduct} disabled={searching} data-testid="product-search-btn">
                  {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                </Button>
              </div>

              {colorOptions.length > 0 && (
                <div className="mb-3">
                  <Label className="text-xs mb-1 block flex items-center justify-between">
                    <span>Available colors ({colorOptions.length})</span>
                    {colorOptions[0]?.brand && (
                      <span className="text-[10px] text-gray-500 font-normal">
                        {colorOptions[0].brand} · {colorOptions[0].style}
                      </span>
                    )}
                  </Label>
                  <div className="grid grid-cols-4 gap-1 max-h-64 overflow-y-auto pr-1">
                    {colorOptions.map((c, i) => {
                      const img = c.image_url || c?.images?.[0];
                      const hex = (c.hex || '').toString();
                      // Vendor hex sometimes uses Pantone refs (e.g. "#412 C"); only treat as a real
                      // hex if it matches a 3/6 digit hex code, otherwise fall back to a neutral gray.
                      const safeHex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(hex) ? hex : '#e5e7eb';
                      return (
                        <button
                          key={i}
                          type="button"
                          onClick={() => {
                            // If the color carries 4-view data (from internal catalog), spread
                            // it across all sides. Otherwise just update the current side.
                            if (c.views && (c.views.front || c.views.back || c.views.left_sleeve || c.views.right_sleeve)) {
                              loadColorAcrossAllSides(c, safeHex);
                              toast.success(`Loaded ${c.color_name} · ${Object.values(c.views).filter(Boolean).length}/4 views`);
                            } else {
                              updateSide(s => ({ ...s, background: img || null, background_color: safeHex }));
                            }
                          }}
                          className="bg-white rounded p-1 hover:ring-2 hover:ring-purple-500 transition flex flex-col items-stretch border border-gray-200"
                          title={`${c.color_name || ''}${c.supplier ? ' · ' + c.supplier : ''}`}
                          data-testid={`color-swatch-${i}`}
                        >
                          <div className="relative w-full h-14 rounded overflow-hidden"
                            style={{ backgroundColor: safeHex }}>
                            {img && (
                              <img src={img} alt={c.color_name || ''}
                                className="absolute inset-0 w-full h-full object-contain"
                                onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                            )}
                          </div>
                          <span className="text-[9px] text-gray-700 mt-0.5 truncate text-center">
                            {c.color_name || ''}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-[10px] text-gray-400 mt-2 leading-tight">
                    {colorOptions[0]?.supplier === 'Internal Catalog'
                      ? 'From your internal catalog — real product photos.'
                      : 'Tip: vendor photos may not load — add this style to your Internal Catalog or use "Upload Custom Product" for a real mockup photo.'}
                  </p>
                </div>
              )}

              <div className="border-t pt-3 space-y-2">
                <label className="block">
                  <input type="file" accept="image/*" onChange={handleBackgroundUpload} className="hidden" data-testid="upload-product-input" />
                  <Button variant="outline" size="sm" className="w-full" asChild>
                    <span><Upload className="w-4 h-4 mr-2" />Upload Custom Product</span>
                  </Button>
                </label>
                <Button variant="outline" size="sm" className="w-full" onClick={useItemImage} data-testid="use-item-image-btn">
                  <ImageIcon className="w-4 h-4 mr-2" />Use Order's Product Image
                </Button>
                {side.background?.startsWith('data:') && (
                  <Button variant="outline" size="sm" className="w-full"
                    onClick={openSaveToCatalog} data-testid="save-to-catalog-btn"
                    style={{ borderColor: '#a78bfa', color: '#6d28d9' }}>
                    <BookmarkPlus className="w-4 h-4 mr-2" />Save to Internal Catalog
                  </Button>
                )}
                {side.background && (
                  <Button variant="ghost" size="sm" className="w-full text-red-600"
                    onClick={() => updateSide(s => ({ ...s, background: null, background_color: null }))}>
                    <Trash2 className="w-4 h-4 mr-2" />Remove Background
                  </Button>
                )}
              </div>
            </div>

            {/* Add Layer panel */}
            <div className="bg-white rounded-xl shadow-sm p-4">
              <h3 className="font-semibold text-sm mb-3 flex items-center gap-2">
                <Plus className="w-4 h-4" />Add Layer
              </h3>
              <div className="space-y-2">
                <label className="block">
                  <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" data-testid="add-image-layer-input" />
                  <Button variant="outline" size="sm" className="w-full" asChild>
                    <span><ImageIcon className="w-4 h-4 mr-2" />Upload Design</span>
                  </Button>
                </label>
                <Button variant="outline" size="sm" className="w-full"
                  onClick={() => setShowDesignLibrary(v => !v)} data-testid="toggle-design-library-btn">
                  <Layers className="w-4 h-4 mr-2" />My Designs ({designs.length})
                </Button>
                {showDesignLibrary && (
                  <div className="grid grid-cols-3 gap-2 max-h-40 overflow-y-auto p-1 bg-gray-50 rounded">
                    {designs.map((d) => (
                      <button key={d.id}
                        onClick={() => addImageLayer(`data:image/png;base64,${d.image_base64}`, d.name)}
                        className="hover:ring-2 hover:ring-purple-500 rounded bg-white">
                        <img src={`data:image/png;base64,${d.image_base64}`} alt={d.name}
                          className="w-full h-14 object-contain" />
                      </button>
                    ))}
                  </div>
                )}
                <Button variant="outline" size="sm" className="w-full" onClick={addTextLayer} data-testid="add-text-layer-btn">
                  <Type className="w-4 h-4 mr-2" />Add Text Layer
                </Button>
              </div>
            </div>
          </div>

          {/* CENTER: Canvas */}
          <div className="col-span-12 lg:col-span-6">
            <div className="bg-white rounded-xl shadow-sm p-4">
              {/* Side tabs */}
              <div className="flex justify-center gap-2 mb-4">
                {SIDES.map(s => (
                  <Button key={s}
                    variant={currentSide === s ? 'default' : 'outline'}
                    size="sm" onClick={() => { setCurrentSide(s); setActiveLayerId(null); }}
                    data-testid={`side-tab-${s}`}>
                    {s.charAt(0).toUpperCase() + s.slice(1)}
                  </Button>
                ))}
              </div>

              {/* Canvas */}
              <div
                ref={canvasRef}
                className="relative rounded-lg overflow-hidden mx-auto select-none"
                style={{
                  width: '100%', maxWidth: CANVAS_SIZE, aspectRatio: '1 / 1',
                  backgroundColor: side.background_color || '#f3f4f6',
                }}
                onMouseDown={onCanvasMouseDown}
                onMouseMove={onCanvasMouseMove}
                onMouseUp={onCanvasMouseUp}
                onMouseLeave={onCanvasMouseUp}
                data-testid="design-canvas"
              >
                {/* Virtual CANVAS_SIZE coordinate space */}
                <div className="absolute inset-0" style={{ width: '100%', height: '100%' }}>
                  {/* Background — show product photo if loaded, otherwise the chosen color fills the canvas */}
                  {side.background ? (
                    <img src={side.background} alt="product"
                      className="absolute inset-0 w-full h-full object-contain pointer-events-none"
                      draggable={false}
                      onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                  ) : !side.background_color ? (
                    <div className="absolute inset-0 flex items-center justify-center text-gray-400">
                      <div className="text-center text-sm">
                        <ImageIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
                        <p>Pick a product to start</p>
                      </div>
                    </div>
                  ) : null}

                  {/* Layers */}
                  {side.layers.map((l) => {
                    if (!l.visible) return null;
                    const isActive = l.id === activeLayerId;
                    const xPct = (l.x / CANVAS_SIZE) * 100;
                    const yPct = (l.y / CANVAS_SIZE) * 100;
                    const wPct = (l.width / CANVAS_SIZE) * 100;
                    const hPct = (l.height / CANVAS_SIZE) * 100;
                    return (
                      <div key={l.id}
                        onMouseDown={(e) => { e.stopPropagation(); setActiveLayerId(l.id); onCanvasMouseDown(e); }}
                        className={`absolute ${l.locked ? 'cursor-not-allowed' : 'cursor-move'} ${isActive ? 'outline outline-2 outline-purple-500' : ''}`}
                        style={{
                          left: `${xPct}%`, top: `${yPct}%`,
                          width: `${wPct}%`, height: `${hPct}%`,
                          transform: `rotate(${l.rotation || 0}deg)`,
                          opacity: l.opacity ?? 1,
                          transformOrigin: 'center center',
                        }}
                      >
                        {l.type === 'image' && l.src && (
                          <img src={l.src} alt="" className="w-full h-full object-contain pointer-events-none" draggable={false} />
                        )}
                        {l.type === 'text' && (
                          <div className="w-full h-full flex items-center justify-center pointer-events-none">
                            {renderTextLayer(l)}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Print area guide */}
                  <div className="absolute border-2 border-dashed border-red-400/40 pointer-events-none"
                    style={{ left: '20%', top: '18%', width: '60%', height: '50%' }} />
                </div>
              </div>

              {/* Item navigation */}
              {proposal?.items?.length > 1 && (
                <div className="flex justify-center items-center gap-4 mt-4 text-sm">
                  <Button variant="outline" size="sm" disabled={itemIndex === 0}
                    onClick={() => navigate(`/mockup-designer?proposal=${proposalId}&item=${itemIndex - 1}`)}>
                    ← Previous Item
                  </Button>
                  <span className="text-gray-600">Item {itemIndex + 1} / {proposal.items.length}</span>
                  <Button variant="outline" size="sm" disabled={itemIndex >= proposal.items.length - 1}
                    onClick={() => navigate(`/mockup-designer?proposal=${proposalId}&item=${itemIndex + 1}`)}>
                    Next Item →
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* RIGHT: Layers + Properties */}
          <div className="col-span-12 lg:col-span-3 space-y-3">
            {/* Layers list */}
            <div className="bg-white rounded-xl shadow-sm p-4">
              <h3 className="font-semibold text-sm mb-3 flex items-center gap-2">
                <Layers className="w-4 h-4" />Layers ({side.layers.length})
              </h3>
              {side.layers.length === 0 && (
                <p className="text-xs text-gray-400 italic">No layers yet. Add a design or text.</p>
              )}
              <div className="space-y-1 max-h-72 overflow-y-auto">
                {[...side.layers].reverse().map((l) => {
                  const isActive = l.id === activeLayerId;
                  return (
                    <div key={l.id}
                      className={`flex items-center gap-1 p-2 rounded cursor-pointer text-xs ${isActive ? 'bg-purple-100 ring-1 ring-purple-400' : 'bg-gray-50 hover:bg-gray-100'}`}
                      onClick={() => setActiveLayerId(l.id)}
                      data-testid={`layer-row-${l.id}`}
                    >
                      <button onClick={(e) => { e.stopPropagation(); updateLayer(l.id, { visible: !l.visible }); }}
                        title={l.visible ? 'Hide' : 'Show'}>
                        {l.visible ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5 text-gray-400" />}
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); updateLayer(l.id, { locked: !l.locked }); }}
                        title={l.locked ? 'Unlock' : 'Lock'}>
                        {l.locked ? <Lock className="w-3.5 h-3.5 text-amber-600" /> : <Unlock className="w-3.5 h-3.5" />}
                      </button>
                      <span className="flex-1 truncate font-medium">
                        {l.type === 'text' ? `T: ${l.text?.slice(0, 14) || 'text'}` : l.name || 'Image'}
                      </span>
                      <button onClick={(e) => { e.stopPropagation(); moveLayer(l.id, 1); }} title="Move up">
                        <ChevronUp className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); moveLayer(l.id, -1); }} title="Move down">
                        <ChevronDown className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); deleteLayer(l.id); }} title="Delete">
                        <Trash2 className="w-3.5 h-3.5 text-red-500" />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Active layer properties */}
            {activeLayer && (
              <div className="bg-white rounded-xl shadow-sm p-4 space-y-3" data-testid="layer-properties">
                <h3 className="font-semibold text-sm flex items-center gap-2">
                  <RotateCw className="w-4 h-4" />Layer Properties
                </h3>

                <div>
                  <Label className="text-xs">Size: {Math.round(activeLayer.width)}px</Label>
                  <Slider value={[activeLayer.width]} min={30} max={CANVAS_SIZE}
                    onValueChange={([v]) => updateActive({
                      width: v,
                      height: activeLayer.type === 'image' ? v : activeLayer.height
                    })}
                    className="mt-1" />
                </div>

                <div>
                  <Label className="text-xs">Rotation: {Math.round(activeLayer.rotation)}°</Label>
                  <Slider value={[activeLayer.rotation]} min={-180} max={180}
                    onValueChange={([v]) => updateActive({ rotation: v })} className="mt-1" />
                </div>

                <div>
                  <Label className="text-xs">Opacity: {Math.round((activeLayer.opacity ?? 1) * 100)}%</Label>
                  <Slider value={[(activeLayer.opacity ?? 1) * 100]} min={10} max={100}
                    onValueChange={([v]) => updateActive({ opacity: v / 100 })} className="mt-1" />
                </div>

                {activeLayer.type === 'text' && (
                  <div className="space-y-2 border-t pt-3">
                    <div>
                      <Label className="text-xs">Text</Label>
                      <Textarea value={activeLayer.text}
                        onChange={(e) => updateActive({ text: e.target.value })}
                        rows={2} data-testid="text-layer-content" />
                    </div>
                    <div>
                      <Label className="text-xs">Font</Label>
                      <Select value={activeLayer.fontFamily} onValueChange={(v) => updateActive({ fontFamily: v })}>
                        <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {FONTS.map(f => <SelectItem key={f} value={f} style={{ fontFamily: f }}>{f}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Font size: {activeLayer.fontSize}px</Label>
                      <Slider value={[activeLayer.fontSize]} min={8} max={120}
                        onValueChange={([v]) => updateActive({ fontSize: v })} className="mt-1" />
                    </div>
                    <div className="flex gap-2 items-center">
                      <Label className="text-xs flex-1">Color</Label>
                      <input type="color" value={activeLayer.color}
                        onChange={(e) => updateActive({ color: e.target.value })}
                        className="w-10 h-8 rounded cursor-pointer" />
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant={activeLayer.bold ? 'default' : 'outline'}
                        onClick={() => updateActive({ bold: !activeLayer.bold })} className="flex-1 h-8">
                        <strong>B</strong>
                      </Button>
                      <Button size="sm" variant={activeLayer.italic ? 'default' : 'outline'}
                        onClick={() => updateActive({ italic: !activeLayer.italic })} className="flex-1 h-8">
                        <em>I</em>
                      </Button>
                      <Button size="sm" variant={activeLayer.stroke ? 'default' : 'outline'}
                        onClick={() => updateActive({ stroke: !activeLayer.stroke })} className="flex-1 h-8">
                        Outline
                      </Button>
                    </div>
                    {activeLayer.stroke && (
                      <>
                        <div className="flex gap-2 items-center">
                          <Label className="text-xs flex-1">Outline color</Label>
                          <input type="color" value={activeLayer.strokeColor}
                            onChange={(e) => updateActive({ strokeColor: e.target.value })}
                            className="w-10 h-8 rounded cursor-pointer" />
                        </div>
                        <div>
                          <Label className="text-xs">Outline width: {activeLayer.strokeWidth}px</Label>
                          <Slider value={[activeLayer.strokeWidth]} min={1} max={10}
                            onValueChange={([v]) => updateActive({ strokeWidth: v })} className="mt-1" />
                        </div>
                      </>
                    )}
                    <div>
                      <Label className="text-xs">Arch / Curve: {activeLayer.arch}</Label>
                      <Slider value={[activeLayer.arch]} min={-100} max={100}
                        onValueChange={([v]) => updateActive({ arch: v })} className="mt-1" />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Save-to-Catalog modal */}
      <Dialog open={showSaveCatalog} onOpenChange={(o) => !o && setShowSaveCatalog(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save to Internal Catalog</DialogTitle>
            <DialogDescription>
              Saves the current canvas background as a colorway for this style. If the style doesn't exist yet, it'll be created.
            </DialogDescription>
          </DialogHeader>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '12px 0' }}>
            {side.background && (
              <div style={{ textAlign: 'center', background: '#f8fafc', borderRadius: 8, padding: 8 }}>
                <img src={side.background} alt="preview"
                  style={{ maxWidth: '100%', maxHeight: 160, objectFit: 'contain' }} />
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Style code *</Label>
                <Input value={catalogForm.style_code}
                  placeholder="NL3600"
                  onChange={(e) => setCatalogForm((f) => ({ ...f, style_code: e.target.value }))}
                  data-testid="catalog-style-code" />
              </div>
              <div>
                <Label className="text-xs">Brand *</Label>
                <Input value={catalogForm.brand}
                  placeholder="Next Level"
                  onChange={(e) => setCatalogForm((f) => ({ ...f, brand: e.target.value }))}
                  data-testid="catalog-brand" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Style name *</Label>
              <Input value={catalogForm.style_name}
                placeholder="Premium Fitted Crew"
                onChange={(e) => setCatalogForm((f) => ({ ...f, style_name: e.target.value }))}
                data-testid="catalog-style-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Color name *</Label>
                <Input value={catalogForm.color_name}
                  placeholder="Heather Gray"
                  onChange={(e) => setCatalogForm((f) => ({ ...f, color_name: e.target.value }))}
                  data-testid="catalog-color-name" />
              </div>
              <div>
                <Label className="text-xs">Hex</Label>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <input type="color" value={catalogForm.hex || '#000000'}
                    onChange={(e) => setCatalogForm((f) => ({ ...f, hex: e.target.value }))}
                    style={{ width: 36, height: 36, padding: 0, border: '1px solid #cbd5e1', borderRadius: 6 }} />
                  <Input value={catalogForm.hex} placeholder="#000000"
                    onChange={(e) => setCatalogForm((f) => ({ ...f, hex: e.target.value }))} />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSaveCatalog(false)} disabled={savingCatalog}>Cancel</Button>
            <Button onClick={saveCanvasToCatalog} disabled={savingCatalog}
              style={{ background: '#7c3aed', color: '#fff' }}
              data-testid="catalog-save-btn">
              {savingCatalog ? <><Loader2 className="animate-spin w-4 h-4 mr-2" />Saving…</> : <><BookmarkPlus className="w-4 h-4 mr-2" />Save to Catalog</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MockupDesignerV2;
